import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { useRecentFixturesStore } from '@/store/recentFixturesStore';
import { RecentFixtureItem } from '@/components/fixtures/RecentFixtureItem';
import { VesselSizeFilter } from '@/components/fixtures/VesselSizeFilter';
import { Button } from '@/components/ui';
import { DragHandle, type DragHandleProps } from './DraggableWidget';

export function RecentFixturesWidget({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) {
  const {
    fixtures,
    loading,
    filters,
    fetchFixtures,
    refreshFixtures,
    setVesselSizeFilter,
    fetchVesselSizeAccess,
    disallowedVesselSizeIds,
  } = useRecentFixturesStore();
  const navigate = useNavigate();
  const [hasRequestedInitialData, setHasRequestedInitialData] = useState(false);

  // Fetch fixtures on mount only
  useEffect(() => {
    setHasRequestedInitialData(true);
    fetchFixtures();
    fetchVesselSizeAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showLoadingState = loading || (!hasRequestedInitialData && fixtures.length === 0);

  // Get only 4 most recent fixtures sorted by reportedTime DESC
  const recentFixtures = useMemo(() => {
    return [...fixtures]
      .sort((a, b) => new Date(b.reportedTime).getTime() - new Date(a.reportedTime).getTime())
      .slice(0, 4);
  }, [fixtures]);

  const handleRefresh = () => {
    refreshFixtures();
  };

  const handleViewAllRecentFixtures = () => {
    navigate('/recent-fixtures');
  };

  return (
    <div className="h-full">
      {/* Header */}
      <div className="-mx-px -mt-px mb-3 flex items-center justify-between rounded-t-lg bg-gray-200/60 px-5 py-3 dark:bg-dark-700">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white sm:text-xl">
            Recent Fixtures
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {dragHandleProps && <DragHandle {...dragHandleProps} />}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="View all Recent Fixtures"
            onClick={handleViewAllRecentFixtures}
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Vessel size filter */}
      <div className="mb-2 px-5 sm:mb-2.5">
        <VesselSizeFilter
          variant="compact"
          value={filters.vesselSizeId ?? 0}
          onChange={setVesselSizeFilter}
          disallowedVesselSizeIds={disallowedVesselSizeIds}
        />
      </div>

      {/* Fixture cards */}
      <div className="space-y-1.5 px-5 pb-5">
        {showLoadingState && recentFixtures.length === 0 ? (
          // Loading skeletons
          Array.from({ length: 4 }).map((_, idx) => (
            <div
              key={idx}
              className="h-[72px] animate-pulse rounded-lg bg-gray-200 dark:bg-dark-700"
            />
          ))
        ) : recentFixtures.length > 0 ? (
          recentFixtures.map((fixture) => (
            <RecentFixtureItem key={fixture.fixtureId} fixture={fixture} hideMoreDetails={true} />
          ))
        ) : (
          <div className="flex h-64 items-center justify-center text-gray-500 dark:text-gray-400">
            No recent fixtures available
          </div>
        )}
      </div>
    </div>
  );
}
